import { Children, type FC, isValidElement, memo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import { ArtifactDisplay } from "~/components/assistant-v2/ArtifactDisplay";
import { HighlightedCodeBlock } from "~/components/assistant-v2/HighlightedCodeBlock";
import { remarkArtifactRefs } from "~/components/assistant/lib/remarkArtifactRefs";
import { cn } from "~/lib/utils";

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm, remarkArtifactRefs]}
      className="aui-md"
      components={componentsWithArtifacts}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

/**
 * Standalone markdown renderer that accepts text directly instead of
 * reading from the assistant-ui part context. Used by {@link LastTextContent}
 * in Thread.tsx so that final response text renders independently of the
 * message running/complete lifecycle.
 */
export const StandaloneMarkdown: FC<{ text: string }> = memo(function StandaloneMarkdown({ text }) {
  return (
    <div className="aui-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkArtifactRefs]}
        components={componentsWithArtifacts}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
});

function extractCodeFromPreChildren(children: ReactNode): { code: string; language: string } {
  const [first] = Children.toArray(children);
  if (!isValidElement<{ className?: string; children?: ReactNode }>(first)) {
    return { code: "", language: "" };
  }
  const rawText = typeof first.props.children === "string" ? first.props.children : "";
  const language = /language-(\w+)/.exec(first.props.className ?? "")?.[1] ?? "";
  return { code: rawText.replace(/\n$/, ""), language };
}

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mt-6 mb-4 scroll-m-20 font-semibold text-2xl leading-snug tracking-tight first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-6 mb-3 scroll-m-20 font-semibold text-xl leading-snug tracking-tight first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-5 mb-2 scroll-m-20 font-semibold text-lg leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-4 mb-2 scroll-m-20 font-semibold text-base leading-snug first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mt-4 mb-1 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mt-4 mb-1 scroll-m-20 font-semibold text-xs uppercase tracking-wide text-muted-foreground first:mt-0 last:mb-0",
        className
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p className={cn("aui-md-p mt-5 mb-5 leading-6 first:mt-0 last:mb-0", className)} {...props} />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn("aui-md-a font-medium text-primary underline underline-offset-4", className)}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote className={cn("aui-md-blockquote border-l-2 pl-6 italic", className)} {...props} />
  ),
  ul: ({ className, ...props }) => (
    <ul className={cn("aui-md-ul my-5 ml-6 list-disc [&>li]:mt-1", className)} {...props} />
  ),
  ol: ({ className, ...props }) => (
    <ol className={cn("aui-md-ol my-5 ml-6 list-decimal [&>li]:mt-1", className)} {...props} />
  ),
  hr: ({ className, ...props }) => (
    <hr className={cn("aui-md-hr my-5 border-b", className)} {...props} />
  ),
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-5 w-full border-separate border-spacing-0 overflow-y-auto",
        className
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-4 py-2 text-left font-bold first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-b border-l px-4 py-2 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right",
        className
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className
      )}
      {...props}
    />
  ),
  sup: ({ className, ...props }) => (
    <sup className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)} {...props} />
  ),
  pre: function Pre({ children }) {
    const { code, language } = extractCodeFromPreChildren(children);
    return <HighlightedCodeBlock code={code} language={language} />;
  },
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            "aui-md-inline-code rounded border bg-muted px-1 font-semibold text-[0.9em]",
          className
        )}
        {...props}
      />
    );
  },
});

// Custom element for the remarkArtifactRefs plugin. react-markdown's
// Components type doesn't include custom element names, so we cast.
const ArtifactRefComponent: FC<{ name: string }> = ({ name }) => (
  <ArtifactDisplay artifactNames={[name]} />
);

const componentsWithArtifacts = {
  ...defaultComponents,
  "artifact-ref": ArtifactRefComponent,
} as unknown as typeof defaultComponents;
